import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";

const WHATSAPP_NUMBER = "551152397247";
const WHATSAPP_MESSAGE = encodeURIComponent(
  "Olá! Gostaria de tirar dúvidas sobre certificado digital."
);
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

// Rotas onde o botão NÃO deve aparecer — páginas de conversão direta
// (produto e checkout). A ideia é não competir com o CTA de compra:
// quem chegou nesse ponto não precisa de um chat paralelo que desviaria
// o foco do fluxo de pagamento.
function isHiddenRoute(pathname: string): boolean {
  if (pathname.startsWith("/produto/")) return true;
  if (pathname === "/checkout") return true;
  if (pathname.startsWith("/admin")) return true;
  return false;
}

export function WhatsAppButton() {
  const location = useLocation();
  const [showTip, setShowTip] = useState(true);
  // Entrada atrasada 3s pra não competir com o CTA principal no first-paint.
  // No mobile o botão sobe pra `bottom-24` pra não colidir com a sticky CTA
  // bar de produto (quando presente na página).
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(t);
  }, []);
  if (!visible) return null;
  if (isHiddenRoute(location.pathname)) return null;

  return (
    <div className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-50 flex items-end gap-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {showTip && (
        <div className="hidden md:flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-xl border border-border max-w-[260px] animate-in fade-in slide-in-from-right-4 duration-500">
          <div className="flex-1">
            <p className="text-sm font-bold text-foreground leading-tight">Dúvidas? Falamos agora.</p>
            <p className="text-xs text-muted-foreground mt-0.5">Resposta humana em ~2 min</p>
          </div>
          <button
            type="button"
            onClick={() => setShowTip(false)}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Fechar"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Falar no WhatsApp"
        className="relative flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
        style={{ backgroundColor: "#25D366" }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 32 32"
          fill="white"
          className="h-8 w-8"
        >
          <path d="M16 2C8.268 2 2 8.268 2 16c0 2.47.643 4.788 1.768 6.8L2 30l7.394-1.74A13.94 13.94 0 0 0 16 30c7.732 0 14-6.268 14-14S23.732 2 16 2zm0 25.6a11.54 11.54 0 0 1-5.884-1.61l-.422-.25-4.388 1.032 1.052-4.274-.276-.44A11.56 11.56 0 0 1 4.4 16C4.4 9.594 9.594 4.4 16 4.4S27.6 9.594 27.6 16 22.406 27.6 16 27.6zm6.34-8.664c-.348-.174-2.058-1.016-2.378-1.132-.32-.116-.552-.174-.784.174-.232.348-.9 1.132-1.102 1.364-.202.232-.404.26-.752.086-.348-.174-1.47-.542-2.8-1.728-1.034-.924-1.732-2.064-1.934-2.412-.202-.348-.022-.536.152-.71.156-.156.348-.406.522-.61.174-.202.232-.348.348-.58.116-.232.058-.434-.028-.61-.086-.174-.784-1.89-1.074-2.59-.282-.68-.57-.588-.784-.598l-.668-.012c-.232 0-.61.086-.928.434-.318.348-1.216 1.188-1.216 2.898s1.246 3.362 1.42 3.594c.174.232 2.452 3.742 5.942 5.246.83.358 1.478.572 1.984.732.834.264 1.594.226 2.194.138.67-.1 2.058-.842 2.348-1.656.29-.814.29-1.512.202-1.656-.086-.144-.318-.232-.666-.406z" />
        </svg>
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-30" style={{ backgroundColor: "#25D366" }} />
        {/* Notification dot */}
        <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow">1</span>
      </a>
    </div>
  );
}
